import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { type FC } from "react";

import { WYSIWYG } from "@fronterahealth/frontera-ui-components";

import {
  AutoTextArea,
  FieldLabelRow,
  InlineInput,
  KebabMenu,
  type MenuItem,
  RULE_GRID,
  ReportTypeChips,
} from "@pages/QARules/RuleEditorParts";
import { type PackageRuleFields, type RuleChip } from "@pages/QARules/qaRuleFields";

export interface PackageRuleItemHandlers {
  onToggleExpand: () => void;
  onChangeField: (patch: Partial<PackageRuleFields>) => void;
  /** Saves immediately with this patch applied (chip toggles, rich-text saves). */
  onSavePatch: (patch: Partial<PackageRuleFields>) => void;
  onToggleChip: (chip: RuleChip) => void;
  onBlurSave: () => void;
  onArchive: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

/**
 * One rule inside a package card: name, IA/RA chips, location, category, what to
 * scan for (rich text, saved as markdown on its own Save) and feedback. Each field
 * is saved as-is; turning them into prompt text happens on the backend.
 */
export const PackageRuleItem: FC<{
  fields: PackageRuleFields;
  expanded: boolean;
  highlighted?: boolean;
  /** IA / RA chips only make sense for ABA packages. */
  showChips: boolean;
  handlers: PackageRuleItemHandlers;
}> = ({ fields, expanded, highlighted, showChips, handlers }) => {
  const menuItems: MenuItem[] = [
    ...(handlers.onMoveUp ? [{ label: "Move up", onClick: handlers.onMoveUp }] : []),
    ...(handlers.onMoveDown ? [{ label: "Move down", onClick: handlers.onMoveDown }] : []),
    { label: "Archive", onClick: handlers.onArchive, danger: true },
  ];
  const chevron = (
    <span className="flex-shrink-0 text-text-tertiary">
      {expanded ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
    </span>
  );
  const kebab = <KebabMenu ariaLabel={`Actions for ${fields.name || "rule"}`} items={menuItems} />;

  return (
    <div
      className={clsx(
        "border-t border-border-subtle",
        expanded && "bg-bg-default/60",
        highlighted && "ring-2 ring-inset ring-globemallow-600",
      )}
    >
      {expanded ? (
        <div className="flex items-center gap-3 px-4 py-2.5 pl-6">
          <button type="button" onClick={handlers.onToggleExpand} aria-label="Collapse rule">
            {chevron}
          </button>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">
            {fields.name || "Untitled rule"}
          </span>
          {kebab}
        </div>
      ) : (
        <div className={clsx(RULE_GRID, "px-4 py-3 pl-6")}>
          <button type="button" onClick={handlers.onToggleExpand} className="flex min-w-0 items-center gap-2 text-left">
            {chevron}
            <span className="truncate text-sm font-medium text-text-primary">{fields.name || "Untitled rule"}</span>
          </button>
          {showChips ? <ReportTypeChips types={fields.chips} editable={false} onToggle={() => {}} /> : <div />}
          <div className="truncate text-sm text-text-tertiary" title={fields.location}>
            {fields.location}
          </div>
          {kebab}
        </div>
      )}

      {expanded && (
        <div className="px-4 pb-4 pl-12 pr-8">
          <div className="grid grid-cols-1 gap-3.5 md:grid-cols-[minmax(0,1.5fr)_140px_minmax(0,1fr)_minmax(0,1fr)]">
            <InlineInput
              label="Rule name"
              value={fields.name}
              onChange={(v) => handlers.onChangeField({ name: v })}
              onBlur={handlers.onBlurSave}
            />
            {showChips ? (
              <div>
                <FieldLabelRow label="Report type" info="The rule is only sent for these report types." />
                <ReportTypeChips types={fields.chips} editable onToggle={handlers.onToggleChip} />
              </div>
            ) : (
              <div />
            )}
            <InlineInput
              label="Location in report"
              value={fields.location}
              onChange={(v) => handlers.onChangeField({ location: v })}
              onBlur={handlers.onBlurSave}
            />
            <InlineInput
              label="Category"
              value={fields.category}
              onChange={(v) => handlers.onChangeField({ category: v })}
              onBlur={handlers.onBlurSave}
            />
          </div>

          <div className="mt-3.5">
            <FieldLabelRow
              label="What to scan for"
              info="Rich text, saved as markdown when you click Save. A rule with nothing here isn't sent."
            />
            <WYSIWYG
              title=""
              showTitle={false}
              placeholder="What the scanner should look for"
              existingContent={fields.scanFor}
              isSaving={false}
              onSave={async ({ content }) => {
                if (content !== fields.scanFor) handlers.onSavePatch({ scanFor: content });
              }}
              containerClassName="mt-0 mb-0"
            />
          </div>
          <AutoTextArea
            label="Feedback message"
            value={fields.feedback}
            info="Text in [brackets] is filled in by the scanner with what it found."
            onChange={(v) => handlers.onChangeField({ feedback: v })}
            onBlur={handlers.onBlurSave}
          />
        </div>
      )}
    </div>
  );
};
